'use client';

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { User, Mail, Loader2 } from "lucide-react";
import { InputWithIcon, PhoneInput } from '@/components/ui/input-icon';
import { registerSchema } from "@/validations/validators";
import { signUp } from "aws-amplify/auth";
import Errors from "@/services/cognito/errors";

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
  });

  function titleCase(str) {
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  }

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      const firstName = titleCase(values.firstName);
      const lastName = titleCase(values.lastName);

      const resp = await signUp({
        username: values.email,
        password: crypto.randomUUID(),
        options: {
          userAttributes: {
            email: values.email.toLowerCase(),
            phone_number: values.phone,
            name: `${firstName} ${lastName}`,
          },
        },
      });

      if (resp.isSignUpComplete) {
        toast.success("Cadastro realizado com sucesso!");
        router.push("/login");
      }

    } catch (err) {
      console.error("RegisterPage", err)
      toast.error(Errors[err.name] ?? Errors["DefaultError"]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-xl mx-auto hover:scale-none bg-background text-foreground ">
      <CardHeader>
        <CardTitle className="text-3xl font-bold text-center">Cadastre-se agora!</CardTitle>
        <CardDescription className={"text-center"}>
          Preencha todos os campos obrigatórios para continuar.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome<span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <InputWithIcon {...field} placeholder="Digite o nome" icon={User} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sobrenome<span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <InputWithIcon {...field} placeholder="Digite o sobrenome" icon={User} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail<span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <InputWithIcon {...field} placeholder="email@exemplo.com" icon={Mail} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone<span className="text-red-500">*</span></FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      defaultCountry="BR"
                      maxLength={15}
                      placeholder="(XX) XXXXX-XXXX"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse items-center gap-4 pt-4 md:flex-row md:justify-between">

              <div className="text-center text-sm">
                Possui uma conta?{" "}
                <Link
                  href={`/login`}
                  className="text-sm font-medium hover:underline break-words whitespace-normal max-w-xs underline underline-offset-4"
                >
                  Entrar
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full md:w-auto cursor-pointer min-w-35"
              >
                Cadastrar
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              </Button>

            </div>
          </form>
        </Form>
      </CardContent>

    </Card>
  );
}
